import { captureException } from '@sentry/vue'
import { SettingsApi } from '../../services'
import { WITH_TEXT, LOGO } from './LOGOS'
import { compareVersions } from './utils'

const DEFAULT_CONFIG = {
  v: process.env.VUE_APP_VERSION,
  anon: parseInt(process.env.VUE_APP_ANON_PID || '1'), //preload before server provides latest
  rl: parseInt(process.env.VUE_APP_REFERRAL_LIMIT || '20'),
  pt: {},
}
const state = {
  isSidebarVisible: false,
  isSidebarEnabled: false,
  isGettingStartedVideoVisible: false,
  isPaymentModalVisible: false,
  isSetupVisible: false,
  isBannerVisible: true,
  config: DEFAULT_CONFIG,
  isLoading: false,
  logo: 1,
  error: null,
  clientVersion: process.env.VUE_APP_VERSION,
  env: process.env.VUE_APP_ENV_NAME,
  toothNumberSystem: 'uns',
  loadIntervalId: null,
}

/*
export interface RemoteConfig {
  v: string //version
  dn: string //domain name
  api: string
  pub: string
  app: string //app url
  dm: number
  anon: number
  pt: Pt
  ts: string,
  rl: number // referral limit
  mscv: string
}

{"v":"3.1.969","dn":"localhost:8080","api":"https://nullable2.loca.lt/api/","pub":"http://localhost:3000/","app":"http://localhost:8080/","dm":349760,"anon":350440,"pt":{"General Dentist":{"plural":"General Dentists","abbr":"GP","reasons":["New Patient Evaluation"]},"Endodontist":{"plural":"Endodontists","abbr":"Endo","reasons":["Pain Evaluation","Root Canal Treatment","Root Canal Treatment: Including post and Build up","Root Canal Treatment: Leave post space","Retreatment","Retreatment: Including post and Build up","Retreatment: Leave post space","Implant Consultation","Evaluate and Treat as Needed"]},"Oral Pathologist":{"plural":"Oral Pathologists","abbr":"Patho","reasons":[]},"Oral Surgeon":{"plural":"Oral Surgeons","abbr":"OMS","reasons":["Pain Evaluation","Extraction","Implant Evaluation","Sinus Lift","Evaluation of Lesion","Panoramic X-Ray","Third Molar Evaluation","Third Molar Extraction"]},"Orthodontist":{"plural":"Orthodontists","abbr":"Ortho","reasons":["Orthodontic Evaluation and Treatment","Aesthetic Evaluation","Crowding/Spacing","Open Bite/Deep Bite","TMJ","Pre Restorative Evaluation","Invisalign Evaluation"]},"Pedodontist":{"plural":"Pedodontists","abbr":"Pedo","reasons":[]},"Periodontist":{"plural":"Periodontists","abbr":"Perio","reasons":["Generalized Perio Evaluation","Localized Perio Evaluation","Mucogingival (Recession) Evaluation","Implant Consultation"]},"Prosthodontist":{"plural":"Prosthodontists","abbr":"Pros","reasons":["Complete Prosthodontic Evaluation","Limited Prosthodontic Evaluation","Implant Reconstruction","Crown and Bridge","Complete Partial Denture","Removable Partial Denture","TMJ"]}},"ts":"2025-03-26T08:22:11.199395Z","rl":1000,"mscv":"3.1.578"}
*/

const mutations = {
  setLoading(state, isLoading) {
    state.isLoading = isLoading
  },
  setError(state, error) {
    state.error = error
  },
  setConfig(state, config) {
    state.config = config
  },
  setSidebar(state, isVisible) {
    state.isSidebarVisible = isVisible
  },
  setSidebarEnabled(state, isEnabled) {
    state.isSidebarEnabled = isEnabled
  },
  setIsGettingStartedVideoVisible(state, isVisible) {
    state.isGettingStartedVideoVisible = isVisible
  },
  setIsPaymentModalVisible(state, isVisible) {
    state.isPaymentModalVisible = isVisible ?? !state.isPaymentModalVisible
  },
  setIsBannerVisible(state, isVisible) {
    state.isBannerVisible = isVisible
  },
  setIsSetupVisible(state, isVisible) {
    state.isSetupVisible = isVisible
  },
  setLogo(state, logo) {
    state.logo = logo
  },
  setToothNumberSystem(state, toothNumberSystem) {
    state.toothNumberSystem = toothNumberSystem
  },
  setVersionCheckIntervalId(state, intervalId) {
    state.versionCheckIntervalId = intervalId
  },
}

const actions = {
  async load({ dispatch, commit }) {
    const intervalId = setInterval(() => {
      dispatch('_load')
    }, 5 * 60 * 1000)
    commit('setVersionCheckIntervalId', intervalId)
    return dispatch('_load')
  },
  async _load({ commit, state, dispatch }) {
    commit('setError', null)
    if (state.config?.ts) return state.config

    commit('setLoading', true)
    let config = null
    try {
      config = await SettingsApi.all({}, { anonymous: true })
      if (config?.anon !== state.config?.anon) {
        console.warn('Config mismatch on server', { l: config?.anon, s: state.config?.anon })
      }
      commit('setConfig', config)

      //check if the client version is the same as the server version
      if (compareVersions(state.clientVersion, config.mscv) < 0) {
        const currentUrl = new URL(window.location.href)
        const requestedVersion = currentUrl.searchParams.get('cv')
        if (requestedVersion) {
          captureException(
            new Error(`Client version updated failed. Request ${requestedVersion} and got ${state.clientVersion}`)
          )
          if (state.env !== 'production') {
            dispatch('notifications/error', 'Update failed. Clear cache and force refresh page.', {
              root: true,
            })
          }
        } else {
          dispatch(
            'notifications/info',
            {
              text: 'Update required. Please reload the page.',
              opts: {
                duration: null,
                action: {
                  text: 'Reload',
                  href: '/',
                  onClick: (e, obj) => {
                    currentUrl.searchParams.set('cv', config.mscv)
                    window.location.href = currentUrl.toString()
                  },
                  dontClose: true,
                },
              },
            },
            { root: true }
          )
        }
      }
    } catch (err) {
      console.error(err)
      commit('setConfig', DEFAULT_CONFIG)
      commit('setError', err)
      commit('setLoading', false)
      throw err
    }
    commit('setLoading', false)
    return config
  },
  setSidebar({ commit, state }, isVisible) {
    commit('setSidebar', isVisible)
  },
  setSidebarEnabled({ commit, state }, isEnabled) {
    commit('setSidebarEnabled', isEnabled)
  },
  toggleSidebar({ commit, state }) {
    commit('setSidebar', !state.isSidebarVisible)
  },
  setIsGettingStartedVideoVisible({ commit, state }, isVisible) {
    commit('setIsGettingStartedVideoVisible', isVisible)
  },
  setIsPaymentModalVisible({ commit, state }, isVisible) {
    commit('setIsPaymentModalVisible', isVisible ?? !state.isPaymentModalVisible)
  },
  setIsBannerVisible({ commit, state }, isVisible) {
    commit('setIsBannerVisible', isVisible)
  },
  setIsSetupVisible({ commit, state, rootState, rootGetters }, isVisible) {
    if(!isVisible && !rootGetters['auth/isSetupDismissable']) return
    commit('setIsSetupVisible', isVisible)
  },
  setToothNumberSystem({ commit }, toothNumberSystem) {
    const tns = {
      0: 'uns',
      1: 'fdi',
      uns: 'uns',
      fdi: 'fdi',
    }

    if (toothNumberSystem in tns) {
      commit('setToothNumberSystem', tns[toothNumberSystem] + '')
    } else {
      console.error('Invalid tooth number system', toothNumberSystem)
      return
    }
  },
  //TODO: Not used but should have a global pubnub channel, anon users too
  stateGlobalUpdate: async ({ commit, state, dispatch }, { type, data }) => {
    if (!type) return
    if (type == 'message') {
      // {text, opts}
      dispatch('notifications/' + (data.type || 'info'), { ...data.message }, { root: true })
    }
    if (type == 'version') {
      if (compareVersions(state.clientVersion, data.version) < 0) {
        dispatch('load')
      }
    }
  },
  onAuthChange: (
    { commit, state, rootGetters },
    { user, oldUser, isPracticeChanged, type }
  ) => {
if(!state.isSetupVisible)
      commit('setIsSetupVisible', !rootGetters['auth/isSetupDismissable'])

  },
}

const getters = {
  PracticeTypes(state) {
    return Object.keys(state.config.pt)
  },
  WithReasons(state) {
    const withReasons = {}
    for (const practiceType in state.config.pt) {
      withReasons[practiceType] = state.config.pt[practiceType].reasons
    }
    return withReasons
  },
  FlatReasons: (state, getters) =>
    Object.keys(getters.WithReasons).reduce((a, c) => ({ ...a, ...getters.WithReasons[c] }), {}),

  PracticeDataArray: (state) =>
    Object.entries(state.config.pt).map(([name, { plural, abbr, reasons }]) => ({
      name: name,
      plural,
      abbr,
      reasons,
    })),

  PracticeTypesPlural(state, getters) {
    return getters.PracticeDataArray.map(({ plural }) => plural)
  },
  PracticeTypesAbbr(state) {
    return Object.values(state.config.pt).map(({ abbr }) => abbr)
  },
  DemoId: (state) => state.config?.dm,
  AnonId: (state) => state.config?.anon,
  Logo: (state) => LOGO[state.logo % LOGO.length],
  LogoText: (state) => WITH_TEXT[state.logo % WITH_TEXT.length],
  AppUrl: state => state.config?.app,
  FormUrl: state => state.config?.frm || state.config?.api,
  IsSetupVisible: (state, getters, root, rootGetters) => state.isSetupVisible || rootGetters['auth/isSetupDismissable'] === false,
}

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
}
